import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '72px 0',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  marginBottom: '12px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  marginBottom: '48px',
  maxWidth: '720px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '32px',
  },
  '& span': {
    color: theme.palette.text.secondary,
  },
}));

const Prose = styled(Typography)(({ theme }) => ({
  fontSize: '1.0625rem',
  lineHeight: 1.85,
  color: theme.palette.text.secondary,
  marginBottom: '20px',
  '& strong': {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  '& a': {
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    transition: 'opacity 0.2s ease',
    '&:hover': { opacity: 0.7 },
  },
}));

const Panel = styled(Box)(({ theme }) => ({
  padding: '28px',
  borderRadius: '14px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  animation: `${fadeIn} 0.5s ease-out`,
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
}));

const PanelLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: '16px',
}));

const ListRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '12px',
  padding: '12px 0',
  borderBottom: `1px dashed ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
  '& .key': {
    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    minWidth: '78px',
    flexShrink: 0,
  },
  '& .val': {
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    fontWeight: 500,
    wordBreak: 'break-word',
  },
}));

const TechRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

const TechChip = styled(Box)(({ theme }) => ({
  padding: '6px 12px',
  borderRadius: '999px',
  border: `1px solid ${theme.palette.divider}`,
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  background: 'transparent',
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
}));

const Bio = () => {
  const facts = [
    { key: 'Based in', val: 'Fargo, ND' },
    { key: 'Currently', val: 'Software Engineer @ Pavewise Pro' },
    { key: 'Education', val: 'MS CS, NDSU (Dec 2025)' },
    { key: 'Researching', val: 'LLM-assisted software testing' },
  ];

  const tech = [
    'TypeScript',
    'React',
    'Ruby on Rails',
    'PostgreSQL',
    'Python',
    'Node.js',
    'FastAPI',
    'Docker',
    'Git',
  ];

  return (
    <Section id="about">
      <Container maxWidth="lg">
        <Eyebrow>About</Eyebrow>
        <SectionHeading>
          Engineer with a research bent.{' '}
          <span>
            I like the parts of the job where you have to think before you type.
          </span>
        </SectionHeading>

        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={7}>
            <Prose>
              I grew up in Nairobi and got into software through a mix of an
              information science degree at the{' '}
              <a
                href="https://tukenya.ac.ke"
                target="_blank"
                rel="noopener noreferrer"
              >
                Technical University of Kenya
              </a>{' '}
              and a Moringa / Flatiron bootcamp in 2023. From there I moved to
              the US for an MS in Computer Science at <strong>NDSU</strong>,
              where I started doing research on combining LLMs with traditional
              testing tools. I wanted better coverage without trusting the
              model blindly.
            </Prose>
            <Prose>
              During grad school I supported faculty and staff at NDSU&apos;s IT
              division. That meant running platform migrations
              (Blackboard Original to Ultra, Zoom Phone), supervising a small
              student team, and writing the kind of docs that mean people stop
              opening tickets. That work taught me to design for the person actually
              using the system, not the person building it.
            </Prose>
            <Prose>
              Today I'm a <strong>full-time software engineer at Pavewise Pro</strong>{' '}
              in Fargo, building cloud applications across React, TypeScript,
              and Ruby on Rails. I care about clear writing, fewer abstractions
              than I think I need, and tests that catch the bugs that actually
              ship.
            </Prose>
          </Grid>

          <Grid item xs={12} md={5}>
            <Panel>
              <PanelLabel>{'// At a glance'}</PanelLabel>
              {facts.map((f) => (
                <ListRow key={f.key}>
                  <span className="key">{f.key}</span>
                  <span className="val">{f.val}</span>
                </ListRow>
              ))}
            </Panel>

            <Box sx={{ mt: 3 }}>
              <Panel>
                <PanelLabel>{'// Tech I reach for'}</PanelLabel>
                <TechRow>
                  {tech.map((t) => (
                    <TechChip key={t}>{t}</TechChip>
                  ))}
                </TechRow>
              </Panel>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};

export default Bio;
